import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { TreatmentPlan } from "@dentacore/shared-types";

import { getPlan } from "../api/plans";
import { TreatmentCard } from "../patients/treatment-panel";
import styles from "./plans.module.css";

/**
 * One procedure course or old treatment plan, read-only — `/plans/:planId`
 * (PV6).
 *
 * **Kept as an address because links point at it**: the tooth editor's
 * "planned on" line, old bookmarks, and anything pasted to a colleague. The
 * plan editor that used to live here is gone, so the same read-only card the
 * patient file shows is drawn instead, with the way back to that patient.
 */
export function PlanHistoryPage(): React.JSX.Element {
  const { planId = "" } = useParams();
  const [plan, setPlan] = useState<TreatmentPlan | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setPlan(null);
    setFailed(false);
    getPlan(planId, controller.signal)
      .then((found) => {
        if (!controller.signal.aborted) setPlan(found);
      })
      .catch(() => {
        if (!controller.signal.aborted) setFailed(true);
      });
    return () => controller.abort();
  }, [planId]);

  return (
    <div className={styles.historyPage} data-testid="plan-history">
      {plan !== null && (
        <Link
          to={`/patients/${plan.patientId}`}
          className={styles.railBack}
          data-testid="plan-history-back"
        >
          ← {plan.patientName}
        </Link>
      )}
      {failed ? (
        <p className={styles.historyNote} data-testid="plan-history-missing">
          That treatment could not be found at this location.
        </p>
      ) : plan === null ? (
        <p className={styles.historyNote}>Reading&hellip;</p>
      ) : (
        <TreatmentCard plan={plan} />
      )}
    </div>
  );
}
